import {Box, Popover} from '@mui/material';
import {
  GridFilterModel,
  gridPageCountSelector,
  gridPageSelector,
  gridPageSizeSelector,
  gridRowCountSelector,
  GridSortModel,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {MOON_500} from '@wandb/weave/common/css/color.styles';
import {useOrgName} from '@wandb/weave/common/hooks/useOrganization';
import {useViewerUserInfo2} from '@wandb/weave/common/hooks/useViewerUserInfo';
import {Radio, Switch} from '@wandb/weave/components';
import {Button} from '@wandb/weave/components/Button';
import {CodeEditor} from '@wandb/weave/components/CodeEditor';
import {
  DraggableGrow,
  DraggableHandle,
} from '@wandb/weave/components/DraggablePopups';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {ToggleButtonGroup} from '@wandb/weave/components/ToggleButtonGroup';
import classNames from 'classnames';
import copyToClipboard from 'copy-to-clipboard';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import * as userEvents from '../../../../../../integrations/analytics/userEvents';
import {Select} from '../../../../../Form/Select';
import {useWFHooks} from '../wfReactInterface/context';
import {Query} from '../wfReactInterface/traceServerClientInterface/query';
import {
  ContentType,
  fileExtensions,
} from '../wfReactInterface/traceServerClientTypes';
import {CallFilter} from '../wfReactInterface/wfDataModelHooksInterface';
import {WFHighLevelCallFilter} from './callsTableFilter';
import {useFilterSortby} from './callsTableQuery';

const MAX_EXPORT = 10_000;

type SelectionState = 'all' | 'selected' | 'limit';

export type CodeExportMode = 'Python' | 'Curl';

const CodeExportSelector: FC<{
  onSelectMode: (mode: CodeExportMode) => void;
}> = ({onSelectMode}) => {
  const [selectedMode, setSelectedMode] = useState<CodeExportMode>('Python');

  React.useEffect(() => {
    onSelectMode(selectedMode);
  }, [selectedMode, onSelectMode]);

  const options = [
    {value: 'Python', icon: 'python-logo' as const},
    {value: 'Curl', icon: 'code-alt' as const},
  ];

  return (
    <div className="flex items-center">
      <ToggleButtonGroup
        options={options}
        value={selectedMode}
        size="medium"
        onValueChange={value => setSelectedMode(value as CodeExportMode)}
      />
    </div>
  );
};

const LabelWithSwitch: FC<{
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  tooltip?: string;
}> = ({id, label, checked, onCheckedChange, disabled, tooltip}) => {
  const content = (
    <div
      className={classNames('flex items-center', disabled ? 'opacity-40' : '')}>
      <Switch.Root
        id={id}
        size="small"
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}>
        <Switch.Thumb size="small" checked={checked} />
      </Switch.Root>
      <label
        htmlFor={id}
        className={classNames('ml-6', disabled ? '' : 'cursor-pointer')}>
        {label}
      </label>
    </div>
  );

  if (tooltip) {
    return <div title={tooltip}>{content}</div>;
  }

  return content;
};

const OptionsDropdown: FC<{
  traceRootsOnly: boolean;
  setTraceRootsOnly: (checked: boolean) => void;
  includeFeedback: boolean;
  setIncludeFeedback: (checked: boolean) => void;
  includeCosts: boolean;
  setIncludeCosts: (checked: boolean) => void;
  disabled?: boolean;
}> = ({
  traceRootsOnly,
  setTraceRootsOnly,
  includeFeedback,
  setIncludeFeedback,
  includeCosts,
  setIncludeCosts,
  disabled,
}) => {
  return (
    <div className="w-full">
      <div className="flex items-center gap-16">
        <LabelWithSwitch
          id="trace-roots-only"
          label="Trace roots only"
          checked={traceRootsOnly}
          onCheckedChange={setTraceRootsOnly}
          tooltip={'Only export the root traces'}
        />
        <LabelWithSwitch
          id="include-feedback"
          label="Feedback"
          checked={includeFeedback}
          onCheckedChange={setIncludeFeedback}
          disabled={disabled}
        />
        <LabelWithSwitch
          id="include-costs"
          label="Costs"
          checked={includeCosts}
          onCheckedChange={setIncludeCosts}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export const ExportSelector = ({
  selectedCalls,
  numTotalCalls,
  visibleColumns,
  refColumnsToExpand,
  callQueryParams,
}: {
  selectedCalls: string[];
  numTotalCalls: number;
  visibleColumns: string[];
  refColumnsToExpand: string[];
  callQueryParams: {
    entity: string;
    project: string;
    filter: WFHighLevelCallFilter;
    gridFilter: GridFilterModel;
    gridSort?: GridSortModel;
  };
}) => {
  const [selectionState, setSelectionState] = useState<SelectionState>('all');
  const [downloadLoading, setDownloadLoading] = useState<ContentType | null>(
    null
  );
  const {loading: viewerLoading, userInfo} = useViewerUserInfo2();
  const userInfoLoaded = !viewerLoading ? userInfo : null;
  const {orgName} = useOrgName({
    entityName: userInfoLoaded?.username ?? '',
    skip: viewerLoading,
  });
  const [traceRootsOnly, setTraceRootsOnly] = useState(
    callQueryParams.filter.traceRootsOnly ?? false
  );
  useEffect(() => {
    setTraceRootsOnly(callQueryParams.filter.traceRootsOnly ?? false);
  }, [callQueryParams.filter.traceRootsOnly]);

  const [includeFeedback, setIncludeFeedback] = useState(false);
  const [includeCosts, setIncludeCosts] = useState(false);

  // Popover management
  const ref = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const onClick = () => {
    setAnchorEl(anchorEl ? null : ref.current);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;

  // Call download query
  const {useCallsExport} = useWFHooks();
  const download = useCallsExport();
  const {sortBy, lowLevelFilter, filterBy} = useFilterSortby(
    callQueryParams.filter,
    callQueryParams.gridFilter,
    callQueryParams.gridSort
  );
  const filterComputed = {
    ...lowLevelFilter,
    callIds:
      selectionState === 'selected' ? selectedCalls : lowLevelFilter.callIds,
    traceRootsOnly,
  };

  const onClickDownload = (contentType: ContentType) => {
    if (downloadLoading) {
      return;
    }
    setDownloadLoading(contentType);
    // TODO(gst): allow specifying offset
    const offset = 0;
    const limit = MAX_EXPORT;

    // Explicitly add feedback column for CSV/TSV exports
    if (
      [ContentType.csv, ContentType.tsv].includes(contentType) &&
      includeFeedback
    ) {
      visibleColumns.push('summary.weave.feedback');
    }

    const leafColumns = [ContentType.csv, ContentType.tsv].includes(contentType)
      ? makeLeafColumns(visibleColumns)
      : undefined;
    const startTime = Date.now();
    download({
      entity: callQueryParams.entity,
      project: callQueryParams.project,
      contentType,
      filter: lowLevelFilter,
      limit,
      offset,
      sortBy,
      query: filterBy,
      columns: leafColumns,
      expandedRefCols: refColumnsToExpand,
      includeFeedback,
      includeCosts,
    }).then(blob => {
      const fileExtension = fileExtensions[contentType];
      const date = new Date().toISOString().split('T')[0];
      const fileName = `weave_export_${callQueryParams.project}_${date}.${fileExtension}`;
      initiateDownloadFromBlob(blob, fileName);
      setAnchorEl(null);
      setDownloadLoading(null);

      userEvents.exportClicked({
        dataSize: blob.size,
        numColumns: visibleColumns?.length ?? null,
        numRows: numTotalCalls,
        numExpandedColumns: refColumnsToExpand.length,
        // the most nested refColumn to expand
        maxDepth: refColumnsToExpand.reduce(
          (max, col) => Math.max(max, col.split('.').length),
          0
        ),
        type: contentType,
        latency: Date.now() - startTime,
        userId: userInfoLoaded?.id ?? '',
        organizationName: orgName,
        username: userInfoLoaded?.username ?? '',
      });
    });
    setSelectionState('all');
  };

  const usedExpandColumns = getUsedExpandColumns(
    refColumnsToExpand,
    filterBy,
    sortBy
  );
  const pythonText = makeCodeText(
    callQueryParams.entity,
    callQueryParams.project,
    selectionState === 'selected' ? selectedCalls : undefined,
    filterComputed,
    filterBy,
    sortBy,
    includeFeedback,
    includeCosts,
    usedExpandColumns
  );
  const curlText = makeCurlText(
    callQueryParams.entity,
    callQueryParams.project,
    selectionState === 'selected' ? selectedCalls : undefined,
    filterComputed,
    filterBy,
    usedExpandColumns,
    sortBy,
    includeFeedback,
    includeCosts
  );

  return (
    <>
      <span ref={ref}>
        <Button icon="download" variant="secondary" onClick={onClick} />
      </span>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              overflow: 'visible',
            },
          },
        }}
        onClose={() => {
          setAnchorEl(null);
          setSelectionState('all');
        }}
        TransitionComponent={DraggableGrow}>
        <Tailwind>
          <div className="min-w-[560px] max-w-[660px] p-12">
            <DraggableHandle>
              <div className="flex items-center pb-8">
                {selectedCalls.length === 0 ? (
                  <div className="flex-auto text-xl font-semibold">
                    Export (
                    {Math.min(numTotalCalls, MAX_EXPORT).toLocaleString()})
                  </div>
                ) : (
                  <div className="flex-auto text-xl font-semibold">Export</div>
                )}
              </div>
            </DraggableHandle>
            <div className="mt-4 flex flex-col gap-2">
              <p className="font-semibold">Include in exports</p>
              {selectedCalls.length > 0 && (
                <SelectionCheckboxes
                  numSelectedCalls={selectedCalls.length}
                  numTotalCalls={numTotalCalls}
                  selectionState={selectionState}
                  setSelectionState={setSelectionState}
                />
              )}
              <OptionsDropdown
                traceRootsOnly={traceRootsOnly}
                setTraceRootsOnly={setTraceRootsOnly}
                includeFeedback={includeFeedback}
                setIncludeFeedback={setIncludeFeedback}
                includeCosts={includeCosts}
                setIncludeCosts={setIncludeCosts}
              />
            </div>
            <DownloadGrid
              pythonText={pythonText}
              curlText={curlText}
              downloadLoading={downloadLoading}
              onClickDownload={onClickDownload}
            />
          </div>
        </Tailwind>
      </Popover>
    </>
  );
};

/**
 * Determines which expandable columns are actually needed based on current filters and sorting.
 *
 * This function optimizes column expansion by only including columns that are referenced
 * in the current query's filters or sorting criteria, rather than expanding all possible
 * columns. It also includes intermediate path levels when deeper nested fields are accessed.
 *
 * @param refColumnsToExpand - Array of column paths that can be expanded (e.g., ['output', 'inputs', 'summary.usage'])
 * @param filterBy - Query object containing filter conditions that may reference expandable columns
 * @param sortBy - Array of sort criteria that may reference expandable columns
 * @returns Array of column paths that should actually be expanded
 *
 * @example
 * // Given these expandable columns:
 * const refColumnsToExpand = ['output', 'inputs', 'summary.usage', 'summary.costs'];
 *
 * // And these query conditions:
 * const sortBy = [{ field: 'output.result.score', direction: 'asc' }];
 * const filterBy = {
 *   $and: [
 *     {$eq: [{ $getField: 'inputs.prompt' }, {$literal: 1}]}
 *     {$contians: [{ $getField: 'summary.model.x' }, {$literal: True}]}
 *   ]
 * };
 *
 * // The function returns:
 * // ['output', 'output.result', 'inputs', 'summary.model']
 * //
 * // Note: 'summary.costs' is excluded since it's not referenced
 * // Note: 'output.result' is included as an intermediate level for 'output.result.score'
 */
function getUsedExpandColumns(
  refColumnsToExpand: string[],
  filterBy: Query | undefined,
  sortBy: Array<{field: string; direction: 'asc' | 'desc'}>
): string[] {
  const usedColumns = new Set<string>();

  // Helper function to find all matching expand columns for a field
  const findMatchingExpandColumns = (field: string): string[] => {
    const matches: string[] = [];

    // Find the base expand column that matches
    for (const expandCol of refColumnsToExpand) {
      if (field.startsWith(expandCol + '.') || field === expandCol) {
        matches.push(expandCol);
      }
    }

    return matches;
  };

  // Check sortBy fields
  for (const sort of sortBy) {
    const matchingColumns = findMatchingExpandColumns(sort.field);
    matchingColumns.forEach(col => usedColumns.add(col));
  }

  // Check filterBy for getField references
  if (filterBy) {
    const extractFieldsFromQuery = (query: any): void => {
      if (!query || typeof query !== 'object') return;

      // Check for getField operations
      if (query.$getField && typeof query.$getField === 'string') {
        const matchingColumns = findMatchingExpandColumns(query.$getField);
        matchingColumns.forEach(col => usedColumns.add(col));
      }

      // Recursively check all properties
      for (const value of Object.values(query)) {
        if (Array.isArray(value)) {
          value.forEach(extractFieldsFromQuery);
        } else {
          extractFieldsFromQuery(value);
        }
      }
    };

    extractFieldsFromQuery(filterBy);
  }

  return Array.from(usedColumns);
}

const SelectionCheckboxes: FC<{
  numSelectedCalls: number;
  numTotalCalls: number;
  selectionState: SelectionState;
  setSelectionState: Dispatch<SetStateAction<SelectionState>>;
}> = ({numSelectedCalls, numTotalCalls, selectionState, setSelectionState}) => {
  return (
    <>
      <div className="mb-4 ml-2" />
      <Radio.Root
        className="flex items-center"
        aria-label="All checked"
        name="all checked"
        onValueChange={(value: SelectionState) => setSelectionState(value)}
        value={selectionState}>
        <Radio.Item id="all-rows" value="all">
          <Radio.Indicator />
        </Radio.Item>
        <label className="flex items-center">
          <span onClick={() => setSelectionState('all')} className="ml-6 mr-12">
            All rows ({numTotalCalls})
          </span>
        </label>
        <label className="flex items-center">
          <Radio.Item id="selected-rows" value="selected">
            <Radio.Indicator />
          </Radio.Item>
          <span
            className="ml-6 mr-12"
            onClick={() => setSelectionState('selected')}>
            Selected rows ({numSelectedCalls})
          </span>
        </label>
      </Radio.Root>
    </>
  );
};

const DownloadGrid: FC<{
  pythonText: string;
  curlText: string;
  downloadLoading: ContentType | null;
  onClickDownload: (contentType: ContentType) => void;
}> = ({pythonText, curlText, downloadLoading, onClickDownload}) => {
  const [codeMode, setCodeMode] = useState<CodeExportMode>('Python');
  const [selectedFormat, setSelectedFormat] = useState<ContentType>(
    ContentType.jsonl
  );

  type FormatOption = {
    value: ContentType;
    label: string;
  };

  const formatOptions: FormatOption[] = [
    {value: ContentType.csv, label: 'CSV'},
    {value: ContentType.json, label: 'JSON'},
    {value: ContentType.tsv, label: 'TSV'},
    {value: ContentType.jsonl, label: 'JSONL'},
  ];

  const selectedFormatOption = formatOptions.find(
    option => option.value === selectedFormat
  );

  return (
    <>
      <p className="mt-16 font-semibold">Export to file</p>
      <div className="mt-4 flex items-center gap-8">
        <div className="w-full">
          <Select<FormatOption>
            size="medium"
            options={formatOptions}
            value={selectedFormatOption}
            onChange={(option: FormatOption | null) => {
              if (option) {
                setSelectedFormat(option.value);
              }
            }}
            isSearchable={false}
            placeholder="Select format"
          />
        </div>
        <div className="w-full">
          <Button
            icon="download"
            variant="secondary"
            onClick={() => onClickDownload(selectedFormat)}
            disabled={downloadLoading !== null}>
            {downloadLoading === selectedFormat ? 'Exporting...' : 'Export'}
          </Button>
        </div>
      </div>
      <p className="mt-16 font-semibold">Export by code</p>
      <div className="mt-4 flex items-center justify-between gap-4">
        <CodeExportSelector onSelectMode={setCodeMode} />
        {codeMode && (
          <Button
            icon="copy"
            variant="ghost"
            size="medium"
            onClick={() => {
              const codeText = codeMode === 'Python' ? pythonText : curlText;
              copyToClipboard(codeText);
              toast('Code copied to clipboard');
            }}
            tooltip="Copy code to clipboard">
            Copy
          </Button>
        )}
      </div>
      {codeMode && (
        <div className="mt-8 flex max-w-full items-center">
          <CodeEditor
            value={codeMode === 'Python' ? pythonText : curlText}
            maxHeight={600}
            language={codeMode === 'Curl' ? 'shell' : 'python'}
            handleMouseWheel={true}
            alwaysConsumeMouseWheel={false}
          />
        </div>
      )}
    </>
  );
};

export const CompareEvaluationsTableButton: FC<{
  onClick: () => void;
  disabled?: boolean;
  tooltipText?: string;
}> = ({onClick, disabled, tooltipText}) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
    }}>
    <Button
      size="medium"
      variant="primary"
      disabled={disabled}
      onClick={onClick}
      icon="chart-scatterplot"
      tooltip={tooltipText}>
      Compare
    </Button>
  </Box>
);

export const CompareTracesTableButton: FC<{
  onClick: () => void;
  disabled?: boolean;
  tooltipText?: string;
}> = ({onClick, disabled, tooltipText}) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
    }}>
    <Button
      size="medium"
      variant="ghost"
      disabled={disabled}
      onClick={onClick}
      icon="chart-scatterplot"
      tooltip={tooltipText}>
      Compare
    </Button>
  </Box>
);

export const BulkDeleteButton: FC<{
  disabled?: boolean;
  onClick: () => void;
}> = ({disabled, onClick}) => {
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
      }}>
      <Button
        variant="ghost"
        size="medium"
        disabled={disabled}
        onClick={onClick}
        tooltip="Select rows with the checkbox to delete"
        icon="delete"
      />
    </Box>
  );
};

export const BulkAddToDatasetButton: FC<{
  onClick: () => void;
  disabled?: boolean;
}> = ({onClick, disabled}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    // Force tooltip to close by blurring the button
    buttonRef.current?.blur();
    onClick();
  };

  return (
    <Button
      ref={buttonRef}
      variant="ghost"
      size="medium"
      onClick={handleClick}
      disabled={disabled}
      tooltip="Add selected rows to a dataset"
      icon="table">
      Add to dataset
    </Button>
  );
};

export const RefreshButton: FC<{
  onClick: () => void;
  disabled?: boolean;
}> = ({onClick, disabled}) => {
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
      }}>
      <Button
        variant="secondary"
        size="medium"
        onClick={onClick}
        disabled={disabled}
        tooltip="Refresh"
        icon="reload-refresh"
      />
    </Box>
  );
};

export const FilterSelectedButton: FC<{
  selectedCalls: string[];
  filter: WFHighLevelCallFilter;
  setFilter: (newFilter: WFHighLevelCallFilter) => void;
  disabled?: boolean;
  clearSelectedCalls?: () => void;
}> = ({selectedCalls, filter, setFilter, disabled, clearSelectedCalls}) => {
  const onFilterSelected = useCallback(() => {
    if (selectedCalls.length === 0) {
      return;
    }

    // Update the high-level filter with the selected call IDs
    setFilter({
      ...filter,
      callIds: selectedCalls,
    });
    clearSelectedCalls?.();
  }, [selectedCalls, filter, setFilter, clearSelectedCalls]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
      }}>
      <Button
        variant="ghost"
        size="medium"
        disabled={disabled || selectedCalls.length === 0}
        onClick={onFilterSelected}
        tooltip="Filter table to show only selected rows"
        icon="filter-alt">
        Filter selected
      </Button>
    </Box>
  );
};

function initiateDownloadFromBlob(blob: Blob, fileName: string) {
  const downloadUrl = URL.createObjectURL(blob);
  // Create a download link and click it
  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(downloadUrl);
}

function makeLeafColumns(visibleColumns: string[]) {
  // Filter columns down to only the most nested, for example
  // ['output', 'output.x', 'output.x.y'] -> ['output.x.y']
  // sort columns by length, longest to shortest
  visibleColumns.sort((a, b) => b.length - a.length);
  const leafColumns: string[] = [];
  for (const col of visibleColumns) {
    if (leafColumns.some(leafCol => leafCol.startsWith(col))) {
      continue;
    }
    leafColumns.push(col);
  }
  return leafColumns;
}

// Helper types for Python code generation
type PythonValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | PythonValue[]
  | {[key: string]: PythonValue};

interface PythonFunctionCall {
  name: string;
  args?: PythonValue[];
  kwargs: Record<string, PythonValue>;
}

/**
 * Converts a JavaScript value to Python syntax.
 *
 * Args:
 *     value: The JavaScript value to convert
 *     indent: Current indentation level (default: 0)
 *
 * Returns:
 *     String representation in Python syntax
 */
function toPythonValue(value: PythonValue, indent: number = 0): string {
  if (value === undefined || value === null) {
    return 'None';
  }

  if (typeof value === 'boolean') {
    return value ? 'True' : 'False';
  }

  if (typeof value === 'string') {
    return JSON.stringify(value); // This handles escaping
  }

  if (typeof value === 'number') {
    return value.toString();
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '[]';
    }

    // Single element arrays always stay on one line
    if (value.length === 1) {
      return `[${toPythonValue(value[0], indent)}]`;
    }

    // Check if it's a simple array that fits on one line
    const simpleArray = value.every(
      v =>
        typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
    );
    const totalLength = value.map(v => toPythonValue(v, 0)).join(', ').length;

    if (simpleArray && totalLength < 60) {
      return `[${value.map(v => toPythonValue(v, 0)).join(', ')}]`;
    }

    // Multi-line array
    const items = value.map(
      v => `${' '.repeat((indent + 1) * 4)}${toPythonValue(v, indent + 1)}`
    );
    return `[\n${items.join(',\n')}\n${' '.repeat(indent * 4)}]`;
  }

  if (typeof value === 'object' && value !== null) {
    const entries = Object.entries(value).filter(([_, v]) => v !== undefined);

    if (entries.length === 0) {
      return '{}';
    }

    // Check if it's a simple dict that fits on one line
    const simpleDict = entries.every(
      ([_, v]) =>
        typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
    );
    const totalLength = entries
      .map(([k, v]) => `"${k}": ${toPythonValue(v, 0)}`)
      .join(', ').length;

    if (simpleDict && totalLength < 60) {
      return `{${entries
        .map(([k, v]) => `"${k}": ${toPythonValue(v, 0)}`)
        .join(', ')}}`;
    }

    // Multi-line dict
    const items = entries.map(
      ([k, v]) =>
        `${' '.repeat((indent + 1) * 4)}"${k}": ${toPythonValue(v, indent + 1)}`
    );
    return `{\n${items.join(',\n')}\n${' '.repeat(indent * 4)}}`;
  }

  return String(value);
}

/**
 * Formats a Python function call with proper indentation.
 *
 * Args:
 *     call: The function call specification
 *     baseIndent: Base indentation level (default: 0)
 *
 * Returns:
 *     Formatted Python function call string
 */
function formatPythonCall(
  call: PythonFunctionCall,
  baseIndent: number = 0
): string {
  const indent = ' '.repeat(baseIndent * 4);
  const paramIndent = ' '.repeat((baseIndent + 1) * 4);

  const params: string[] = [];

  // Add positional arguments
  if (call.args) {
    params.push(...call.args.map(arg => toPythonValue(arg, baseIndent + 1)));
  }

  // Add keyword arguments
  const kwargEntries = Object.entries(call.kwargs).filter(
    ([_, v]) => v !== undefined
  );
  params.push(
    ...kwargEntries.map(
      ([key, value]) => `${key}=${toPythonValue(value, baseIndent + 1)}`
    )
  );

  if (params.length === 0) {
    return `${indent}${call.name}()`;
  }

  // Check if it fits on one line
  const oneLine = `${indent}${call.name}(${params.join(', ')})`;
  if (oneLine.length < 80 && !params.some(p => p.includes('\n'))) {
    return oneLine;
  }

  // Multi-line format
  return `${indent}${call.name}(\n${params
    .map(p => `${paramIndent}${p}`)
    .join(',\n')}\n${indent})`;
}

function makeCodeText(
  entity: string,
  project: string,
  callIds: string[] | undefined,
  filter: CallFilter,
  query: Query | undefined,
  sortBy: Array<{field: string; direction: 'asc' | 'desc'}>,
  includeFeedback: boolean,
  includeCosts: boolean,
  refColumnsToExpand: string[]
) {
  // Build the imports and client initialization
  let code = `import weave\n\nclient = weave.init("${entity}/${project}")\n`;

  // Build the full call specification
  const callSpec: PythonFunctionCall = {
    name: 'calls = client.get_calls',
    kwargs: {},
  };

  const filterObj: Record<string, PythonValue> = {
    ...(filter.traceRootsOnly === true ? {trace_roots_only: true} : {}),
    ...(filter.opVersionRefs && filter.opVersionRefs.length > 0
      ? {op_names: filter.opVersionRefs}
      : {}),
    ...(filter.runIds && filter.runIds.length > 0
      ? {wb_run_ids: filter.runIds}
      : {}),
    ...(filter.userIds && filter.userIds.length > 0
      ? {wb_user_ids: filter.userIds}
      : {}),
    ...(filter.traceId ? {trace_ids: [filter.traceId]} : {}),
    ...(filter.parentIds && filter.parentIds.length > 0
      ? {parent_ids: filter.parentIds}
      : {}),
    ...(filter.callIds && filter.callIds.length > 0
      ? {call_ids: filter.callIds}
      : {}),
    ...(filter.inputObjectVersionRefs &&
    filter.inputObjectVersionRefs.length > 0
      ? {input_refs: filter.inputObjectVersionRefs}
      : {}),
    ...(filter.outputObjectVersionRefs &&
    filter.outputObjectVersionRefs.length > 0
      ? {output_refs: filter.outputObjectVersionRefs}
      : {}),
  };

  if (Object.keys(filterObj).length > 0) {
    callSpec.kwargs.filter = filterObj;
  }
  if (query) {
    callSpec.kwargs.query = query;
  }
  if (sortBy.length > 0) {
    callSpec.kwargs.sort_by = sortBy;
  }
  if (includeFeedback) {
    callSpec.kwargs.include_feedback = true;
  }
  if (includeCosts) {
    callSpec.kwargs.include_costs = true;
  }
  if (refColumnsToExpand.length > 0) {
    callSpec.kwargs.expand_columns = refColumnsToExpand;
  }

  code += formatPythonCall(callSpec);
  return code;
}

function makeCurlText(
  entity: string,
  project: string,
  callIds: string[] | undefined,
  filter: CallFilter,
  query: Query | undefined,
  expandColumns: string[],
  sortBy: Array<{field: string; direction: 'asc' | 'desc'}>,
  includeFeedback: boolean,
  includeCosts: boolean
) {
  const baseUrl = (window as any).CONFIG.TRACE_BACKEND_BASE_URL;
  const hasOpNames = filter.opVersionRefs && filter.opVersionRefs.length > 0;
  const hasInputRefs =
    filter.inputObjectVersionRefs && filter.inputObjectVersionRefs.length > 0;
  const hasOutputRefs =
    filter.outputObjectVersionRefs && filter.outputObjectVersionRefs.length > 0;
  const hasParentIds = filter.parentIds && filter.parentIds.length > 0;
  const hasTraceIds = filter.traceId;
  const hasCallIds = callIds && callIds.length > 0;
  const hasRunIds = filter.runIds && filter.runIds.length > 0;
  const hasUserIds = filter.userIds && filter.userIds.length > 0;

  const filterStr = JSON.stringify(
    {
      op_names: hasOpNames ? filter.opVersionRefs : undefined,
      input_refs: hasInputRefs ? filter.inputObjectVersionRefs : undefined,
      output_refs: hasOutputRefs ? filter.outputObjectVersionRefs : undefined,
      parent_ids: hasParentIds ? filter.parentIds : undefined,
      trace_ids: hasTraceIds ? [filter.traceId] : undefined,
      call_ids: hasCallIds ? callIds : undefined,
      trace_roots_only: filter.traceRootsOnly,
      wb_run_ids: hasRunIds ? filter.runIds : undefined,
      wb_user_ids: hasUserIds ? filter.userIds : undefined,
    },
    null,
    0
  );

  let baseCurl = `# Ensure you have a WANDB_API_KEY set in your environment
curl '${baseUrl}/calls/stream_query' \\
  -u api:$WANDB_API_KEY \\
  -H 'content-type: application/json' \\
  --data-raw '{
    "project_id":"${entity}/${project}",
    "filter":${filterStr},
`;
  if (query) {
    baseCurl += `    "query":${JSON.stringify(query, null, 0)},\n`;
  }
  if (expandColumns.length > 0) {
    baseCurl += `    "expand_columns":${JSON.stringify(
      expandColumns,
      null,
      0
    )},\n`;
  }
  baseCurl += `    "limit":${MAX_EXPORT},
    "offset":0,
    "sort_by":${JSON.stringify(sortBy, null, 0)},
    "include_feedback": ${includeFeedback},
    "include_costs": ${includeCosts}
  }'`;

  return baseCurl;
}

type PageSizeOption = {
  readonly value: number;
  readonly label: string;
};

type PaginationButtonsProps = {
  hideControls?: boolean;
};

export const PaginationButtons = ({hideControls}: PaginationButtonsProps) => {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);

  const handlePrevPage = () => {
    apiRef.current.setPage(page - 1);
  };

  const handleNextPage = () => {
    apiRef.current.setPage(page + 1);
  };

  // Calculate the item range being displayed
  const start = rowCount > 0 ? page * pageSize + 1 : 0;
  const end = Math.min(rowCount, (page + 1) * pageSize);

  const pageSizes = [10, 25, 50, 100];
  if (!pageSizes.includes(pageSize)) {
    pageSizes.push(pageSize);
    pageSizes.sort((a, b) => a - b);
  }
  const pageSizeOptions = pageSizes.map(sz => ({
    value: sz,
    label: sz.toString(),
  }));
  const pageSizeValue = pageSizeOptions.find(o => o.value === pageSize);
  const onPageSizeChange = (option: PageSizeOption | null) => {
    if (option) {
      apiRef.current.setPageSize(option.value);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      padding={1}>
      <Box display="flex" alignItems="center">
        <Button
          variant="ghost"
          size="medium"
          onClick={handlePrevPage}
          disabled={page === 0}
          icon="chevron-back"
        />
        <Box
          mx={1}
          sx={{
            fontSize: '14px',
            fontWeight: '400',
            color: MOON_500,
            // This is so that when we go from 1-100 -> 101-200, the buttons don't jump
            minWidth: '90px',
            display: 'flex',
            justifyContent: 'center',
          }}>
          {start}-{end} of {rowCount}
        </Box>
        <Button
          variant="ghost"
          size="medium"
          onClick={handleNextPage}
          disabled={page >= pageCount - 1}
          icon="chevron-next"
        />
      </Box>
      {hideControls ? null : (
        <Box
          sx={{
            fontSize: '14px',
            fontWeight: '400',
            color: MOON_500,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            // Regrettable hack to appear over Material scrollbar's z-index of 6.
            zIndex: 7,
          }}>
          Per page:
          <Select<PageSizeOption>
            size="small"
            menuPlacement="top"
            options={pageSizeOptions}
            value={pageSizeValue}
            isSearchable={false}
            onChange={onPageSizeChange}
          />
        </Box>
      )}
    </Box>
  );
};
